import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Blocks, Activity, Coins } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass p-6 rounded-sm hover:border-primary/30 transition-all duration-300"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-text-muted text-sm mb-2">{title}</p>
          <h3 className="font-unbounded text-3xl font-bold text-text-primary">
            {value}
          </h3>
        </div>
        <div className={`p-3 rounded-sm bg-${color}/10`}>
          <Icon size={24} className={`text-${color}`} strokeWidth={1.5} />
        </div>
      </div>
    </motion.div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBlocks, setRecentBlocks] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, blockchainRes, txRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/blockchain`),
        axios.get(`${API}/transactions`)
      ]);

      setStats(statsRes.data);
      setRecentBlocks(blockchainRes.data.blocks.slice(-5).reverse());
      setRecentTransactions(txRes.data.transactions.slice(-10).reverse());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          Network Dashboard
        </h1>
        <p className="text-text-secondary">Monitor your blockchain in real-time</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Blocks"
          value={stats?.total_blocks || 0}
          icon={Blocks}
          color="primary"
        />
        <StatCard
          title="Total Transactions"
          value={stats?.total_transactions || 0}
          icon={Activity}
          color="accent-success"
        />
        <StatCard
          title="Pending Transactions"
          value={stats?.pending_transactions || 0}
          icon={TrendingUp}
          color="accent-warning"
        />
        <StatCard
          title="Total Tokens"
          value={stats?.total_tokens || 0}
          icon={Coins}
          color="secondary"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass p-6 rounded-sm">
          <h2 className="font-unbounded text-xl font-bold mb-4 text-text-primary">
            Recent Blocks
          </h2>
          <div className="space-y-3">
            {recentBlocks.map((block) => (
              <div
                key={block.index}
                data-testid={`block-${block.index}`}
                className="p-4 bg-background-subtle rounded-sm border border-border hover:border-primary/30 transition-colors duration-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm text-primary">Block #{block.index}</span>
                  <span className="text-xs text-text-muted">
                    {new Date(block.timestamp * 1000).toLocaleTimeString()}
                  </span>
                </div>
                <p className="font-mono text-xs text-text-secondary truncate">
                  {block.hash}
                </p>
                <div className="mt-2 flex items-center gap-4">
                  <span className="text-xs text-text-muted">
                    Txs: {block.transactions.length}
                  </span>
                  <span className="text-xs text-text-muted">
                    Nonce: {block.nonce}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass p-6 rounded-sm">
          <h2 className="font-unbounded text-xl font-bold mb-4 text-text-primary">
            Recent Transactions
          </h2>
          <div className="space-y-3">
            {recentTransactions.slice(0, 5).map((tx, idx) => (
              <div
                key={idx}
                data-testid={`transaction-${idx}`}
                className="p-4 bg-background-subtle rounded-sm border border-border hover:border-primary/30 transition-colors duration-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-sm font-medium">
                    {tx.tx_type}
                  </span>
                  <span className="text-xs text-text-muted">
                    {new Date(tx.timestamp * 1000).toLocaleTimeString()}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">From:</span>
                    <span className="font-mono text-text-secondary truncate max-w-[200px]">
                      {tx.sender.substring(0, 12)}...
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">To:</span>
                    <span className="font-mono text-text-secondary truncate max-w-[200px]">
                      {tx.recipient.substring(0, 12)}...
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted text-xs">Amount:</span>
                    <span className="font-bold text-accent-success">
                      {tx.amount} {tx.token_symbol}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
